export interface OcrField {
  value: string
  confidence: number
}

export interface FieldConfidenceDetail {
  key: string
  value: string | undefined
  confidence: number
  threshold: number
  passed: boolean
}

export interface ConfidenceEvaluationResult {
  passed: boolean
  failedFields: FieldConfidenceDetail[]
  evaluatedFields: FieldConfidenceDetail[]
  missingExpectedFields: string[]
}

export interface OcrResult {
  fields: Record<string, OcrField>
  provider: string
  pageCount: number
}

export interface SpecimenItem {
  order: number
  letter: string
  subsite?: string
  description?: string
  type?: string
  confidence: number
}

export interface InsuranceFields {
  CompanyName?: OcrField
  PolicyNumber?: OcrField
  MedicareNumber?: OcrField
  NameOfInsured?: OcrField
  Relationship?: OcrField
}

export interface OcrProcessingResult {
  ocrResult: OcrResult
  mappedFields: Record<string, OcrField>
  mapping: Record<string, string | null>
  confidenceEvaluation: ConfidenceEvaluationResult
  autoSubmit: boolean
  specimens: SpecimenItem[]
  insurance: InsuranceFields
}

export const FIELD_LABELS: Record<string, string> = {
  LastName: 'Last Name',
  FirstName: 'First Name',
  MiddleInitial: 'Middle Initial',
  DateOfBirth: 'Date of Birth',
  Gender: 'Gender',
  Address1: 'Address Line 1',
  Address2: 'Address Line 2',
  City: 'City',
  State: 'State',
  PostalCode: 'Postal Code',
  TelephoneNumber: 'Phone Number',
  RealSSN: 'SSN',
  SecondID: 'MRN',
  AccountNumber: 'Account Number',
  MaritalStatus: 'Marital Status',
  Ethnicity: 'Ethnicity',
  Race: 'Race',
  Language: 'Language',
  Age: 'Age',
  Phone2: 'Cell Phone',
  Country: 'Country',
  ProcedureName: 'Procedure Name',
  RequisitionNumber: 'Requisition Number',
  DateCollected: 'Date Collected',
  ClinicalHistory: 'Clinical History',
  ReferringPhysician: 'Referring Physician',
  SubmittingFacility: 'Submitting Facility',
  CPTCode: 'CPT Code',
}

export const REQUIRED_FIELD_KEYS = [
  'LastName',
  'FirstName',
  'DateOfBirth',
  'Gender',
  'Address1',
  'City',
  'State',
  'PostalCode',
  'TelephoneNumber',
] as const

export const EXPECTED_FIELD_KEYS = [
  'MiddleInitial',
  'RealSSN',
  'Address2',
  'Country',
  'SecondID',
  'AccountNumber',
  'MaritalStatus',
  'Ethnicity',
  'Race',
  'Language',
  'Age',
  'Phone2',
] as const

export const CASE_FIELD_KEYS = [
  'ProcedureName',
  'RequisitionNumber',
  'DateCollected',
  'ClinicalHistory',
  'ReferringPhysician',
  'SubmittingFacility',
  'CPTCode',
] as const

export const INSURANCE_FIELD_KEYS = [
  'CompanyName',
  'PolicyNumber',
  'MedicareNumber',
  'NameOfInsured',
  'Relationship',
] as const

export const INSURANCE_FIELD_LABELS: Record<string, string> = {
  CompanyName: 'Insurance Company',
  PolicyNumber: 'Policy Number',
  MedicareNumber: 'Medicare Number',
  NameOfInsured: 'Insured Name',
  Relationship: 'Relationship',
}

export type WorkflowStatus = 'detected' | 'in_review' | 'approved' | 'published'

/** Client-only status while upload/OCR is in flight. */
export type UiDocumentStatus = WorkflowStatus | 'uploading'

export interface DocumentListItem {
  id: string
  filename: string
  status: UiDocumentStatus
  autoSubmit: boolean
  createdAt: string
  updatedAt: string
  approvedAt: string | null
  patientName?: string | null
}

export interface DocumentDetail {
  id: string
  filename: string
  mimeType: string
  status: WorkflowStatus
  autoSubmit: boolean
  mappedFields: Record<string, OcrField>
  specimens: SpecimenItem[]
  insurance: InsuranceFields
  confidenceEvaluation: ConfidenceEvaluationResult
  createdAt: string
  updatedAt: string
  approvedAt: string | null
}

export const getPatientNameFromFields = (
  mappedFields: Record<string, OcrField>
): string => {
  const first = mappedFields['FirstName']?.value ?? ''
  const last = mappedFields['LastName']?.value ?? ''
  if (first && last) return `${first} ${last}`
  if (last) return last
  return '—'
}

export const buildPatchMappedFields = (
  original: Record<string, OcrField>,
  edited: Record<string, string>
): Record<string, OcrField> => {
  const result: Record<string, OcrField> = { ...original }

  for (const [key, value] of Object.entries(edited)) {
    const existing = original[key]
    if (existing) {
      result[key] = { ...existing, value }
    } else if (value.trim()) {
      result[key] = { value, confidence: 1 }
    }
  }

  return result
}
